import {useMutation, useQuery} from "@apollo/client";
import {useCallback, useEffect, useMemo, useRef} from "react";
import toast from "react-hot-toast";
import {useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {CHECKS_LIST_CUSTOM_VIEWS} from "@/lib/featureFlags.ts";
import {DEFAULT_SORT_KEY} from "@/lib/hooks/useSorting.ts";
import {
  ChecksListAvailableColumnsData,
  ChecksListViewsData,
  DeleteChecksListViewResult,
  GQL_CHECKS_LIST_AVAILABLE_COLUMNS,
  GQL_CHECKS_LIST_VIEW_DELETE,
  GQL_CHECKS_LIST_VIEW_SAVE,
  GQL_CHECKS_LIST_VIEW_SET_DEFAULT,
  GQL_CHECKS_LIST_VIEWS,
  SaveChecksListViewResult,
} from "@/lib/queries/checksListViews.ts";
import {ChecksListView} from "@/lib/types/checksListViews.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

const VIEW_PARAM = "view";
const BUILT_IN_DEFAULT_VIEW_PARAM = "default";

export const useChecksListViews = () => {
  const {userHasFeature} = useCurrentUserContext();
  const isEnabled = userHasFeature(CHECKS_LIST_CUSTOM_VIEWS);
  const [queryParams, setQueryParams] = useSearchParams();

  const {
    data: viewsData,
    loading: viewsLoading,
    refetch: refetchViews,
  } = useQuery<ChecksListViewsData>(GQL_CHECKS_LIST_VIEWS, {
    skip: !isEnabled,
  });
  const isLoading = isEnabled && !viewsData && viewsLoading;

  const {data: columnsData} = useQuery<ChecksListAvailableColumnsData>(GQL_CHECKS_LIST_AVAILABLE_COLUMNS, {
    skip: !isEnabled,
  });

  const [saveMutation] = useMutation<SaveChecksListViewResult>(GQL_CHECKS_LIST_VIEW_SAVE);
  const [deleteMutation] = useMutation<DeleteChecksListViewResult>(GQL_CHECKS_LIST_VIEW_DELETE);
  const [setDefaultMutation] = useMutation<DeleteChecksListViewResult>(GQL_CHECKS_LIST_VIEW_SET_DEFAULT);

  const views = useMemo(() => viewsData?.checksListViews?.items ?? [], [viewsData]);
  const availableColumns = useMemo(() => columnsData?.checksListAvailableColumns ?? [], [columnsData]);

  const viewParam = queryParams.get(VIEW_PARAM);
  const builtInDefaultSelected = viewParam === BUILT_IN_DEFAULT_VIEW_PARAM;
  const activeViewId = viewParam && !builtInDefaultSelected ? parseInt(viewParam, 10) : null;

  const activeView: ChecksListView | undefined = useMemo(() => {
    if (!isEnabled || views.length === 0) return undefined;
    //built-in Default selected
    if (builtInDefaultSelected) return undefined;
    if (activeViewId) {
      return views.find((v) => v.id === activeViewId);
    }
    return views.find((v) => v.is_default);
  }, [isEnabled, views, activeViewId, builtInDefaultSelected]);

  // Save and delete write the view after a server round trip. The params of the render that started them would undo a
  // search, filter or page change made meanwhile, so the write starts from the latest URL.
  const latestParamsRef = useRef(queryParams);
  useEffect(() => {
    latestParamsRef.current = queryParams;
  }, [queryParams]);

  const setViewParam = useCallback(
    (value: string | undefined) => {
      const params = new URLSearchParams(latestParamsRef.current);
      if (value) {
        params.set(VIEW_PARAM, value);
      } else {
        params.delete(VIEW_PARAM);
      }
      // The list then sorts by the new View's default, in this one navigation.
      params.delete(DEFAULT_SORT_KEY);
      setQueryParams(params);
    },
    [setQueryParams]
  );

  const selectView = useCallback(
    (viewId: number | null) => {
      setViewParam(viewId === null ? BUILT_IN_DEFAULT_VIEW_PARAM : String(viewId));
    },
    [setViewParam]
  );

  const saveView = useCallback(
    async (
      id: number | null,
      data: {
        name: string;
        columns: string[];
        default_sort?: string;
        default_sort_direction?: string;
        is_default?: boolean;
      }
    ) => {
      const result = await saveMutation({
        variables: {id, data},
      });
      const response = result.data?.response;
      if (response?.status === ResponseStatus.ERROR) {
        toast.error("Failed to save view.");
        return response;
      }
      toast.success(id ? "View updated." : "View created.");
      await refetchViews();
      if (id === null && response) {
        selectView(response.data.id);
      }
      return response;
    },
    [saveMutation, refetchViews, selectView]
  );

  const deleteView = useCallback(
    async (id: number) => {
      const result = await deleteMutation({variables: {id}});
      const status = result.data?.response?.status;
      if (status === ResponseStatus.ERROR) {
        toast.error("Failed to delete view.");
        return;
      }
      toast.success("View deleted.");
      await refetchViews();
      if (latestParamsRef.current.get(VIEW_PARAM) === String(id)) {
        // Clear the param so the list falls back to the user's configured default view.
        setViewParam(undefined);
      }
    },
    [deleteMutation, refetchViews, setViewParam]
  );

  //TODO this should be possible without hacking to use the save endpoint and instead use the set default endpoint
  const setDefaultView = useCallback(
    async (id: number | null) => {
      // Clearing the default: re-save the currently-default view with is_default = false
      // so the built-in Default option becomes the effective default.
      if (id === null) {
        const currentDefault = views.find((v) => v.is_default);
        if (!currentDefault) return;
        const result = await saveMutation({
          variables: {
            id: currentDefault.id,
            data: {
              name: currentDefault.name,
              columns: currentDefault.columns,
              default_sort: currentDefault.default_sort,
              default_sort_direction: currentDefault.default_sort_direction,
              is_default: false,
            },
          },
        });
        const status = result.data?.response?.status;
        if (status === ResponseStatus.ERROR) {
          toast.error("Failed to set default view.");
          return;
        }
        toast.success("Default view updated.");
        await refetchViews();
        return;
      }

      const result = await setDefaultMutation({variables: {id}});
      const status = result.data?.response?.status;
      if (status === ResponseStatus.ERROR) {
        toast.error("Failed to set default view.");
        return;
      }
      toast.success("Default view updated.");
      await refetchViews();
    },
    [setDefaultMutation, saveMutation, refetchViews, views]
  );

  return {
    isEnabled,
    isLoading,
    views,
    activeView,
    availableColumns,
    selectView,
    saveView,
    deleteView,
    setDefaultView,
  };
};
